import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../../i18n/i18n';
import { appendDictation, getSpeechAdapter, type SpeechLang, type SpeechSession } from '../../native/speech';
import { addCommentAction, insertComment, markCommentUsed, sortedComments, useComments } from '../../state/commentLibrary';
import { SheetShell, TextInput, showToast } from '../../ui/components';
import { Icon } from '../../ui/components/Icon';

// The row under a text field (Owner decision list 2026-10-19 items 32 / 33): 🎤 voice input with the phone's
// own recogniser, 「评语库」 to insert one of the inspector's saved comments, 「存为评语」 to keep what is written.
// `lang` is the language the field is written in: English for what the client report prints, the App
// Language for the inspector's own notes; the inspector can switch it before speaking.
// `compact`: the field is folded (nothing written yet), so 「存为评语」 — which has nothing to keep — is left out.
export function TextAssist(props: { value: string; onChange: (next: string) => void; fieldId: string; lang: SpeechLang; testId: string; disabled?: boolean; compact?: boolean }) {
  const { t } = useT();
  const [lang, setLang] = useState<SpeechLang>(props.lang);
  const [listening, setListening] = useState(false);
  const [keyboardHint, setKeyboardHint] = useState(false);
  const [speechError, setSpeechError] = useState<string | null>(null);
  const [picker, setPicker] = useState(false);
  const session = useRef<SpeechSession | null>(null);
  // Dictated text arrives while the inspector may also type: always append to the latest value.
  const latest = useRef(props.value);
  latest.current = props.value;
  useEffect(() => () => session.current?.stop(), []);

  function toggleSpeech() {
    setSpeechError(null);
    if (listening) {
      session.current?.stop();
      return;
    }
    const adapter = getSpeechAdapter();
    const started = adapter.available()
      ? adapter.start(lang, {
          onText: (text) => props.onChange(appendDictation(latest.current, text, lang)),
          onEnd: () => {
            session.current = null;
            setListening(false);
          },
          // Android acceptance 2026-10-21 (D-15): an error never leaves the button on 「正在听」 — a web view
          // that refuses the microphone reports the error and may never send its end event.
          onError: (code) => {
            session.current?.stop();
            session.current = null;
            setListening(false);
            if (code === 'unavailable') {
              document.getElementById(props.fieldId)?.focus();
              setKeyboardHint(true);
              return;
            }
            setSpeechError(code === 'denied' ? t('assist.speechDenied') : code === 'noSpeech' ? t('assist.speechNoSpeech') : t('assist.speechFailed'));
          }
        })
      : null;
    if (started) {
      session.current = started;
      setListening(true);
      setKeyboardHint(false);
      return;
    }
    // No recogniser in this web view: the keyboard's microphone is the phone's own recogniser.
    document.getElementById(props.fieldId)?.focus();
    setKeyboardHint(true);
  }

  function saveAsComment() {
    const outcome = addCommentAction(props.value);
    showToast(outcome === 'added' ? t('assist.saved') : outcome === 'exists' ? t('assist.exists') : outcome === 'full' ? t('assist.full') : t('assist.emptyToSave'));
  }

  return (
    <div className="text-assist" data-testid={props.testId}>
      <div className="text-assist-row">
        <button type="button" className={`text-assist-btn${listening ? ' text-assist-btn-on' : ''}`} onClick={toggleSpeech} disabled={props.disabled} aria-pressed={listening} data-testid={`${props.testId}-mic`}>
          <Icon name="mic" />
          <span>{listening ? t('assist.listening') : t('assist.speak')}</span>
        </button>
        <button type="button" className="text-assist-lang" onClick={() => setLang(lang === 'en-US' ? 'zh-CN' : 'en-US')} disabled={props.disabled || listening} aria-label={t('assist.switchLang')} data-testid={`${props.testId}-lang`}>
          {lang === 'en-US' ? t('assist.langEn') : t('assist.langZh')}
        </button>
        <button type="button" className="text-assist-btn" onClick={() => setPicker(true)} disabled={props.disabled} data-testid={`${props.testId}-library`}>
          <Icon name="notes" />
          <span>{t('assist.library')}</span>
        </button>
        {props.compact === true ? null : (
          <button type="button" className="text-assist-btn" onClick={saveAsComment} disabled={props.disabled || props.value.trim().length === 0} data-testid={`${props.testId}-save`}>
            <Icon name="add" />
            <span>{t('assist.saveAsComment')}</span>
          </button>
        )}
      </div>
      {keyboardHint ? (
        <p className="field-hint" data-testid={`${props.testId}-keyboard-hint`}>
          {t('assist.keyboardMic')}
        </p>
      ) : null}
      {speechError ? (
        <p className="field-error" role="alert">
          {speechError}
        </p>
      ) : null}
      {picker ? (
        <CommentPickerSheet
          onPick={(text) => {
            props.onChange(insertComment(latest.current, text));
            setPicker(false);
          }}
          onClose={() => setPicker(false)}
        />
      ) : null}
    </div>
  );
}

export function CommentPickerSheet(props: { onPick: (text: string) => void; onClose: () => void }) {
  const { t } = useT();
  const comments = useComments();
  const [query, setQuery] = useState('');
  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortedComments(comments).filter((c) => q.length === 0 || c.text.toLowerCase().includes(q));
  }, [comments, query]);
  return (
    <SheetShell title={t('assist.pickerTitle')} subtitle={t('assist.pickerSubtitle')} onClose={props.onClose} layer="top" testId="comment-picker">
      {comments.length > 0 ? <TextInput value={query} onChange={setQuery} placeholder={t('assist.search')} testId="comment-picker-search" /> : null}
      {comments.length === 0 ? (
        <p className="card-note" data-testid="comment-picker-empty">
          {t('assist.pickerEmpty')}
        </p>
      ) : (
        <div className="option-list">
          {list.map((c) => (
            <button
              key={c.id}
              type="button"
              className="option-row comment-option"
              onClick={() => {
                markCommentUsed(c.id);
                props.onPick(c.text);
              }}
              data-testid="comment-picker-row"
            >
              <span>{c.text}</span>
            </button>
          ))}
        </div>
      )}
    </SheetShell>
  );
}
